import { Link } from "react-router-dom";
import Layout from "../../components/site/Layout";
import Seo from "../../components/Seo";
import "./Guide.css";

const ALL = {
  "/guides/what-is-an-eps-file": "What Is an EPS File?",
  "/guides/how-to-open-eps-files": "How to Open EPS Files",
  "/guides/open-eps-without-illustrator": "Open EPS Without Illustrator",
  "/guides/convert-eps-to-png": "Convert EPS to PNG",
  "/guides/convert-eps-to-jpg": "Convert EPS to JPG",
  "/guides/convert-eps-to-pdf": "Convert EPS to PDF",
  "/guides/eps-vs-svg-vs-ai-vs-pdf": "EPS vs SVG vs AI vs PDF",
  "/guides/vector-vs-raster": "Vector vs Raster Graphics",
  "/guides/fix-eps-file-problems": "Fix Common EPS Problems",
  "/guides/eps-file-format-explained": "EPS File Format Explained",
  "/guides/best-free-eps-editors": "Best Free EPS Editors in 2026",
  "/guides/how-to-create-an-eps-file": "How to Create an EPS File",
  "/guides/eps-files-for-logo-design": "EPS Files for Logo Design",
};

/**
 * Byline shown on every guide. A named person rather than an organization
 * credit, so the schema.org type is "Person" and `worksFor` carries the
 * publisher. Keep the two in step if the byline ever changes back.
 */
const AUTHOR = {
  name: "Vaibhav Singhal",
  type: "Person",
  role: "Co-founder & developer, MagicApps",
  bio:
    "Vaibhav Singhal builds EPS Viewer at MagicApps, a small studio in India " +
    "making free tools for opening and converting design files. He has been " +
    "shipping EPS, CDR, PSD and AI viewers on the web, Android and iOS since " +
    "2021, and these guides come out of the questions users actually send to " +
    "our support inbox.",
};

export default function GuideLayout({ title, description, path, updated, related = [], children }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    datePublished: "2026-06-15",
    dateModified: updated || "2026-06-15",
    author: {
      "@type": AUTHOR.type,
      name: AUTHOR.name,
      jobTitle: AUTHOR.role,
      worksFor: { "@type": "Organization", name: "MagicApps", url: "https://magicapps.co.in/" },
    },
    publisher: { "@type": "Organization", name: "EPS Viewer" },
    // Trailing slash to match the canonical (the site serves <route>/index.html).
    mainEntityOfPage: "https://epsviewer.in" + (path.endsWith("/") ? path : `${path}/`),
  };

  return (
    <Layout>
      <Seo title={`${title} | EPS Viewer`} description={description} path={path} jsonLd={jsonLd} />

      <article className="guide">
        <div className="container guide__container">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/guides">Guides</Link>
            <span>›</span>
            <span className="breadcrumb__current">{title}</span>
          </nav>

          <h1 className="guide__title">{title}</h1>
          <p className="guide__meta">
            By {AUTHOR.name}
            {updated && <> · Updated {updated}</>}
          </p>

          <div className="guide__prose">{children}</div>

          <div className="guide__cta card">
            <div>
              <h3>Need to open an EPS right now?</h3>
              <p>Use the free EPS Viewer — preview your file and download it as a PNG in seconds.</p>
            </div>
            <Link to="/" className="btn btn--primary">Open EPS Viewer</Link>
          </div>

          <aside className="guide__author">
            <h2>About the author</h2>
            <p className="guide__author-role">
              <strong>{AUTHOR.name}</strong> — {AUTHOR.role}
            </p>
            <p>{AUTHOR.bio}</p>
            <p>
              Spotted something wrong in this guide?{" "}
              <Link to="/contact">Tell us</Link> — we correct and re-date our guides
              when the software or the operating systems change.
            </p>
          </aside>

          {related.length > 0 && (
            <div className="guide__related">
              <h2>Related guides</h2>
              <div className="guide__related-grid">
                {related.map((to) => (
                  <Link key={to} to={to} className="guide__related-card">
                    <span>{ALL[to] || to}</span>
                    <span aria-hidden="true">→</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </Layout>
  );
}
